/**
 * Copyright (C) 2022 Viasat, Inc.
 * All rights reserved.
 * The information in this software is subject to change without notice and should not be construed as a commitment by Viasat, Inc.
 *
 * Viasat Proprietary
 * The Proprietary Information provided herein is proprietary to Viasat and
 * must be protected from further distribution and use. Disclosure to others,
 * use or copying without express written authorization of Viasat, is strictly prohibited.
 */
import Highcharts from 'highcharts';
import isNil from 'lodash/isNil';
import merge from 'lodash/merge';
import { ReactElement } from 'react';
import { renderToStaticMarkup } from 'react-dom/server';
import { TerminalTypeLabel } from '../../../../../shared/entities/terminal';
import { setTheAllProtocolModalData } from '../../../../../store/terminalHistory/actions';
import { RateKeys } from '../../../../../store/terminalHistory/reducer';
import { bpsToUnitString } from '../../../../../utils/DataRateUtils';
import { storeAccess } from '../../../../../utils/StoreUtils';
import ViewAllProtocols from '../../Protocols/ViewAllProtocols';
import ChartCustomTooltip from '../ChartCustomComponents/ChartCustomTooltip';
import { getTooltipPointFormatterWithBps } from '../ChartDataFormatterUtils';
import { CHART_HEIGHT } from '../ChartsContainerWrapper';
import { ChartItemKeys, chartSeriesContent, ChartType } from '../ChartSeriesItems';
import ChartWrapper from './ChartWrapper';
import {
  defaultChartOptions,
  getSeriesNameSuffix,
  withTerminalHistoryChartHOCAndNoDataAvailableHOC,
} from './WithTerminalHistoryChartHOC';

interface RlProtocolUsageChartProps {
  index: number;
  text: string;
  id: string;
  chart: Highcharts.Chart | null;
  titlePrefixTerminalType?: TerminalTypeLabel;
  children: ReactElement | ReactElement[];
}

const RlProtocolUsageChart = (props: RlProtocolUsageChartProps) => {
  const { index, text, id, children, titlePrefixTerminalType } = props;
  let chartName = text;

  if (!isNil(titlePrefixTerminalType)) {
    chartName = `${getSeriesNameSuffix(titlePrefixTerminalType)}${chartName}`;
  }

  return (
    <ChartWrapper
      name={chartName}
      id={id}
      index={index}
      chartType={ChartItemKeys.rLProtocolUsage}
      height={CHART_HEIGHT.LARGE}
    >
      {children}
      <ViewAllProtocols type={RateKeys.RL_RATES} terminalType={titlePrefixTerminalType} />
    </ChartWrapper>
  );
};

const getChartOptions = () => {
  const defaultOptions = { ...defaultChartOptions(ChartType.area, true) };

  return merge(defaultOptions, {
    yAxis: {
      labels: {
        formatter(): string {
          // @ts-ignore
          return bpsToUnitString(this.value);
        },
      },
      title: {
        text: 'RL Data Usage',
      },
    },
    plotOptions: {
      area: {
        stacking: 'normal',
      },
      series: {
        events: {
          click: (data: any) => {
            const sourceTime = data.point.x;

            const isOtherTermHistUsed = data.point.series.options.custom.isUsingOtherTerminalHistory;
            storeAccess
              .get()
              ?.dispatch(setTheAllProtocolModalData(sourceTime / 1000, RateKeys.RL_RATES, isOtherTermHistUsed));
          },
        },
      },
    },
    chart: {
      events: {
        click: (data: any) => {
          const xPoint = data.xAxis[0].value / 1000;
          const isOtherTermHistUsed = data.yAxis[0].axis.series[0].options.custom.isUsingOtherTerminalHistory;
          const resetZoomEvent = data.path?.[1].classList?.value?.includes('highcharts-reset-zoom');
          let distance = Number.MAX_VALUE;
          let sourceTime = xPoint;
          let onlyUseSecondaryTerminalHistory =
            data.yAxis[0].axis.series[0].options.custom.onlyUseSecondaryTerminalHistory;
          let termHistory = data.yAxis[0].axis.series[0].options.custom.getPrimaryTerminalHistory?.();

          if (onlyUseSecondaryTerminalHistory) {
            termHistory = data.yAxis[0].axis.series[0].options.custom.getSecondaryTerminalHistory?.();
          }

          if (termHistory) {
            Object.keys(termHistory)
              .filter((timestamp: string) => {
                return !!termHistory[timestamp][RateKeys.RL_RATES];
              })
              .forEach((timestamp: string) => {
                const time = parseInt(timestamp);
                let diff = Math.abs(xPoint - time);
                if (diff <= distance) {
                  distance = diff;
                  sourceTime = time;
                }
              });
          }

          if (!isNil(sourceTime) && !resetZoomEvent) {
            storeAccess.get()?.dispatch(setTheAllProtocolModalData(sourceTime, RateKeys.RL_RATES, isOtherTermHistUsed));
          }
        },
      },
    },
    tooltip: {
      pointFormatter: getTooltipPointFormatterWithBps,
      formatter: function (): string {
        return renderToStaticMarkup(<ChartCustomTooltip data={this} showGraphs={true} sortByYValue={true} />);
      },
    },
  });
};

export { RlProtocolUsageChart as RlProtocolUsageChartComponent, getChartOptions as getRlProtocolUsageChartOptions };

export default withTerminalHistoryChartHOCAndNoDataAvailableHOC(
  RlProtocolUsageChart,
  ChartType.area,
  getChartOptions(),
  chartSeriesContent[ChartItemKeys.rLProtocolUsage],
  'trafficCompGapSec',
  CHART_HEIGHT.LARGE,
  true,
  true,
);
